import React, { memo, useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Fade,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import StopIcon from "@mui/icons-material/Stop";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import { MaintenanceResultType } from "@app/constants";
import { IconDelete } from "@app/components/icons";
import { IconDeleteRed } from "@app/components/icons/IconDeleteRed/IconDeleteRed";
import { IconWarningFormEdit } from "@app/components/icons";
import { IconNotApplicableDashInspection } from "@app/components/icons/IconMaintenace/IconMaintenanceNew";
import { IconSelectedNew } from "@app/components/icons/IconSelected/IconSelectedNew";
import { REGEX } from "@app/helpers";
import { MediaUpload } from "@app/pages/Features/GroupList/GroupDetail/Components/InspectionForm/MediaUpload/MediaUpload";
import type { TemplateInspectionItemMedia } from "../Inspection/createInspectionForm.types";
import {
  getMaintenanceItemDisplayName,
  isMaintenanceCommentWithinLimit,
  isNumericValueValid,
  MAINTENANCE_ACTION_RADIO_SX,
  MaintenanceItemAction,
  MaintenanceItemEntry,
  ManualMaintenanceItemEntry,
  normalizeNumericOnBlur,
  REFILL_AMOUNT_MAX,
  sanitizeNumericInputValue,
  shouldHideMaintenanceRefillReplaceFields,
} from "./maintenanceItemShared";

const INSPECTION_MEDIA_TOTAL_LIMIT = 50;

const ItemAudioPlayerLayout = memo(function ItemAudioPlayerLayout({
  src,
  audioRef,
  onRemove,
}: {
  src: string;
  audioRef?: React.RefObject<HTMLAudioElement>;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div style={{ marginTop: "12px", touchAction: "auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <audio ref={audioRef} controls src={src} style={{ flex: 1 }} />
        <Tooltip title={t("formAction.delete")}>
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              color: "#d32f2f",
              marginLeft: 2,
              borderRadius: "50%",
              transition: "all 0.3s ease",
              "&:hover": {
                border: "2px solid #d32f2f",
                backgroundColor: "transparent",
              },
            }}
          >
            <IconDeleteRed />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
});

const ItemBlobAudio = memo(function ItemBlobAudio({
  blob,
  onRemove,
}: {
  blob: Blob;
  onRemove: () => void;
}) {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [src, setSrc] = useState("");

  useEffect(() => {
    if (!blob?.size) {
      setSrc("");
      return;
    }
    const url = URL.createObjectURL(blob);
    setSrc(url);
    const el = audioRef.current;
    if (el) {
      el.src = url;
      el.load();
    }
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  if (!src) return null;

  return (
    <ItemAudioPlayerLayout src={src} audioRef={audioRef} onRemove={onRemove} />
  );
});

const RecordingTimerBadge = memo(function RecordingTimerBadge({
  active,
  paused,
  startTimeMs,
}: {
  active: boolean;
  paused: boolean;
  startTimeMs: number | null;
}) {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active || !startTimeMs) {
      setElapsed(0);
      return;
    }

    const tick = () => {
      setElapsed(Math.min(Math.floor((Date.now() - startTimeMs) / 1000), 60));
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [active, startTimeMs]);

  if (!active || elapsed >= 60) return null;

  if (paused) {
    return (
      <Tooltip title={t("paused")} arrow>
        <div
          className="glow-yellow"
          style={{
            marginTop: "4px",
            fontSize: "12px",
            color: "#f9a825",
            backgroundColor: "#fff8e1",
            padding: "4px 8px",
            borderRadius: "4px",
            boxShadow: "0 0 8px #fbc02d",
            fontWeight: "bold",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            height: "32px",
            width: "60px",
          }}
        >
          <PauseCircleIcon fontSize="small" />
          {`${elapsed}s`}
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={t("recording")} arrow>
      <div
        className="glow-red"
        style={{
          marginTop: "4px",
          fontSize: "12px",
          color: "#d32f2f",
          backgroundColor: "#ffe6e6",
          padding: "4px 8px",
          borderRadius: "4px",
          boxShadow: "0 0 8px #f44336",
          fontWeight: "bold",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: "32px",
          width: "60px",
        }}
      >
        <FiberManualRecordIcon fontSize="small" />
        <span style={{ transform: "translateY(1px)" }}>{`${elapsed}s`}</span>
      </div>
    </Tooltip>
  );
});

const ResultIconControls = memo(function ResultIconControls({
  itemId,
  resultValue,
  onPick,
}: {
  itemId: string;
  resultValue: MaintenanceResultType | undefined;
  onPick: (itemId: string, value: MaintenanceResultType | undefined) => void;
}) {
  const { t } = useTranslation();

  return (
    <Box
      className="radio-group-container"
      sx={{
        flexShrink: 0,
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        gap: "20px",
        overflowX: "auto",
      }}
    >
      <Tooltip title={t("notPerformed")} arrow>
        <Box
          onClick={() =>
            onPick(
              itemId,
              resultValue === MaintenanceResultType.NOT_PERFORMED
                ? undefined
                : MaintenanceResultType.NOT_PERFORMED,
            )
          }
          sx={{
            width: 50,
            height: 50,
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <IconNotApplicableDashInspection
            fillColor={
              resultValue === MaintenanceResultType.NOT_PERFORMED
                ? "#696969"
                : "#D3D3D3"
            }
            dashColor="white"
            size={resultValue === MaintenanceResultType.NOT_PERFORMED ? 44 : 37}
          />
        </Box>
      </Tooltip>
      <Tooltip title={t("performed")} arrow>
        <Box
          onClick={() =>
            onPick(
              itemId,
              resultValue === MaintenanceResultType.PERFORMED
                ? undefined
                : MaintenanceResultType.PERFORMED,
            )
          }
          sx={{
            width: 50,
            height: 50,
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <IconSelectedNew
            fillColor={
              resultValue === MaintenanceResultType.PERFORMED
                ? "#0e7c47"
                : "#DAFBE1"
            }
            iconColor={
              resultValue === MaintenanceResultType.PERFORMED
                ? "white"
                : "#1A7F37"
            }
          />
        </Box>
      </Tooltip>
    </Box>
  );
});

type NumericItemRowProps = {
  item: MaintenanceItemEntry;
  validationAttempted: boolean;
  onPatchDraft: (
    itemId: string,
    patch: Partial<ManualMaintenanceItemEntry>,
  ) => void;
};

type NonNumericItemRowProps = NumericItemRowProps & {
  itemIndex: number;
  mediaAvailable: boolean;
  isRecording: boolean;
  isPaused: boolean;
  recordingForItemId: string | null;
  recordingStartTimeMs: number | null;
  totalInspectionMediaCount: number;
  mediaPreviewNonce: number;
  uploadDescription: string[];
  onToggleCommentSection: (itemId: string) => void;
  onResultIconPick: (
    itemId: string,
    value: MaintenanceResultType | undefined,
  ) => void;
  onStartRecording: (itemId: string) => void;
  onStopRecording: () => void;
  onRequestDeleteRecording: (itemId: string) => void;
  onPreviewTemplateMedia: (media: TemplateInspectionItemMedia) => void;
  onPreviewUploadMedia: (url: string | string[]) => void;
  onMediaFilesChange: (itemId: string, files: File[]) => void;
  onFileCountChange: (
    itemId: string,
    count: number | ((prev: number) => number),
  ) => void;
  onUploadErrorChange: (itemId: string, hasError: boolean) => void;
  onRemoveItem: (itemId: string) => void;
  setItemRef: (itemId: string, el: HTMLDivElement | null) => void;
};

export const MaintenanceNonNumericItemRow = memo(
  function MaintenanceNonNumericItemRow({
    item,
    itemIndex,
    validationAttempted,
    mediaAvailable,
    isRecording,
    isPaused,
    recordingForItemId,
    recordingStartTimeMs,
    totalInspectionMediaCount,
    mediaPreviewNonce,
    uploadDescription,
    onPatchDraft,
    onToggleCommentSection,
    onResultIconPick,
    onStartRecording,
    onStopRecording,
    onRequestDeleteRecording,
    onPreviewTemplateMedia,
    onPreviewUploadMedia,
    onMediaFilesChange,
    onFileCountChange,
    onUploadErrorChange,
    onRemoveItem,
    setItemRef,
  }: NonNumericItemRowProps) {
    const { t } = useTranslation();

    const thisItemRecording = recordingForItemId === item.id;
    const recordStartDisabled =
      !mediaAvailable ||
      (isRecording && !thisItemRecording) ||
      isPaused ||
      !!item.recordedBlob;
    const showRecordingControls =
      mediaAvailable && !item.recordedBlob && !item.existingItemRecording;

    const showWhitespaceCommentError =
      validationAttempted && item.comment !== "" && !item.comment.trim();
    const showInvalidCommentError = validationAttempted && !!item.commentError;

    useEffect(() => {
      if (
        (showInvalidCommentError || showWhitespaceCommentError) &&
        !item.commentExpanded
      ) {
        onToggleCommentSection(item.id);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showInvalidCommentError, showWhitespaceCommentError]);

    const handleCommentChange = useCallback(
      (value: string) => {
        if (!isMaintenanceCommentWithinLimit(value)) {
          return;
        }
        const isInvalid =
          !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
          !REGEX.emojiRegex.test(value);
        onPatchDraft(item.id, {
          comment: value,
          commentError: isInvalid,
        });
      },
      [item.id, onPatchDraft],
    );

    const handleActionChange = useCallback(
      (value: MaintenanceItemAction) => {
        onPatchDraft(item.id, { action: value });
      },
      [item.id, onPatchDraft],
    );

    const handleRefillAmountChange = useCallback(
      (value: string, fallback: string) => {
        onPatchDraft(item.id, {
          refillAmount: sanitizeNumericInputValue(value, fallback),
        });
      },
      [item.id, onPatchDraft],
    );

    const handleRefillAmountBlur = useCallback(
      (value: string) => {
        const normalized = normalizeNumericOnBlur(value, REFILL_AMOUNT_MAX);
        if (normalized !== value) {
          onPatchDraft(item.id, { refillAmount: normalized });
        }
      },
      [item.id, onPatchDraft],
    );

    const showRefillReplaceFields =
      item.source === "manual" &&
      !shouldHideMaintenanceRefillReplaceFields(item.categoryCode);

    const refillAmountVal =
      item.source === "manual" ? item.refillAmount.trim() : "";
    const refillAmountOverMax =
      !!refillAmountVal &&
      Number.isFinite(parseFloat(refillAmountVal)) &&
      parseFloat(refillAmountVal) > REFILL_AMOUNT_MAX;
    const refillAmountFormatInvalid =
      !!refillAmountVal &&
      !isNumericValueValid(refillAmountVal, REFILL_AMOUNT_MAX);
    const refillAmountShowError =
      showRefillReplaceFields && refillAmountFormatInvalid;
    const refillAmountHelperText = refillAmountOverMax
      ? t("createInspection.numericMax", { max: REFILL_AMOUNT_MAX })
      : refillAmountFormatInvalid
        ? t("invalidCharacters")
        : "";

    const handleSetFileCount = useCallback(
      (n: number | ((prev: number) => number)) => {
        onFileCountChange(item.id, n);
      },
      [item.id, onFileCountChange],
    );

    const handleSetUploadError = useCallback(
      (err: boolean) => {
        onUploadErrorChange(item.id, err);
      },
      [item.id, onUploadErrorChange],
    );

    const handleSetMedias = useCallback(
      (files: File[]) => {
        onMediaFilesChange(item.id, files);
      },
      [item.id, onMediaFilesChange],
    );

    const fileUploadLimit =
      totalInspectionMediaCount -
      item.mediaFiles.length -
      (item.existingItemMedias?.length ?? 0);
    const mediaLimitExceeded =
      totalInspectionMediaCount > INSPECTION_MEDIA_TOTAL_LIMIT;

    return (
      <Box
        ref={(el: HTMLDivElement | null) => setItemRef(item.id, el)}
        sx={{
          pt: itemIndex > 0 ? 2 : 0,
          borderTop: itemIndex > 0 ? "1px solid #e0e0e0" : "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "common.black",
                mb: 0.5,
                textTransform: "capitalize",
                wordWrap: "break-word",
              }}
            >
              {getMaintenanceItemDisplayName(item)}
              {item.source === "schedule" && ` ${item.work}`}
            </Typography>
          </Box>
          <ResultIconControls
            itemId={item.id}
            resultValue={item.resultValue}
            onPick={onResultIconPick}
          />
        </Box>
        {!!item.resultValue && (
          <>
            <Button
              variant="text"
              fullWidth
              onClick={() => onToggleCommentSection(item.id)}
              startIcon={
                item.commentExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
              }
              sx={{
                mt: 2,
                py: 1,
                px: 1,
                textTransform: "none",
                fontWeight: 600,
                justifyContent: "center",
                color: "#696969",
                transition:
                  "background-color 0.25s ease, box-shadow 0.25s ease, color 0.2s ease",
                "&:hover": { backgroundColor: "action.hover" },
                "& .MuiButton-startIcon": {
                  marginRight: "8px",
                  marginLeft: 0,
                  transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                },
              }}
            >
              {item.source === "manual"
                ? t("announcementTable.detail")
                : t("addcommentfile")}
            </Button>
            <Collapse
              in={item.commentExpanded}
              timeout={{ enter: 420, exit: 280 }}
              easing={{
                enter: "cubic-bezier(0.4, 0, 0.2, 1)",
                exit: "cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Fade
                in={item.commentExpanded}
                timeout={{ enter: 360, exit: 200 }}
                easing={{
                  enter: "cubic-bezier(0.4, 0, 0.2, 1)",
                  exit: "cubic-bezier(0.4, 0, 1, 1)",
                }}
              >
                <Box sx={{ mt: 2 }}>
                  {showRefillReplaceFields && item.source === "manual" && (
                    <>
                      <FormControl
                        component="fieldset"
                        sx={{ mt: 0.5, width: "100%" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                          }}
                        >
                          <RadioGroup
                            row
                            value={item.action}
                            sx={{
                              flexWrap: "nowrap",
                              gap: 6,
                              margin: 0,
                              "& .MuiFormControlLabel-root": {
                                margin: 0,
                              },
                            }}
                            onChange={(e) =>
                              handleActionChange(
                                e.target.value as MaintenanceItemAction,
                              )
                            }
                          >
                            <FormControlLabel
                              value="refill"
                              control={<Radio sx={MAINTENANCE_ACTION_RADIO_SX} />}
                              label={t("dialogMaintenanceReports.yesRefill")}
                            />
                            <FormControlLabel
                              value="replace"
                              control={<Radio sx={MAINTENANCE_ACTION_RADIO_SX} />}
                              label={t("dialogMaintenanceReports.yesReplace")}
                            />
                          </RadioGroup>
                        </Box>
                      </FormControl>

                      <TextField
                        className={`input-field ${
                          refillAmountShowError ? "error-border" : ""
                        }`}
                        label={t("dialogMaintenanceReports.refillAmount")}
                        type="text"
                        inputMode="decimal"
                        fullWidth
                        variant="outlined"
                        value={item.refillAmount}
                        onChange={(e) =>
                          handleRefillAmountChange(
                            e.target.value,
                            item.refillAmount,
                          )
                        }
                        onBlur={() => handleRefillAmountBlur(item.refillAmount)}
                        error={refillAmountShowError}
                        helperText={
                          refillAmountShowError ? refillAmountHelperText : ""
                        }
                        FormHelperTextProps={{
                          className: refillAmountShowError ? "error-text" : undefined,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">L</InputAdornment>
                          ),
                        }}
                        sx={{
                          mt: 2,
                          "& .MuiOutlinedInput-input": {
                            textAlign: "right",
                            direction: "ltr",
                            paddingRight: "8px",
                          },
                        }}
                      />
                    </>
                  )}
                  <div
                    className="wrapper-text-field mt-8"
                    style={{ position: "relative" }}
                  >
                    <TextField
                      multiline
                      rows={3}
                      className="input-field"
                      label={t("dialogReport.comment")}
                      value={item.comment}
                      fullWidth
                      onChange={(e) => handleCommentChange(e.target.value)}
                      error={showInvalidCommentError || showWhitespaceCommentError}
                      helperText={
                        showInvalidCommentError
                          ? t("invalidCharacters")
                          : showWhitespaceCommentError
                            ? ""
                            : ""
                      }
                      InputProps={
                        showRecordingControls
                          ? { style: { paddingRight: "65px" } }
                          : undefined
                      }
                      inputProps={{ style: { paddingRight: "20px" } }}
                    />

                    {showRecordingControls && (
                        <div
                          style={{
                            position: "absolute",
                            right: 12,
                            top: 40,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            zIndex: 10,
                            pointerEvents: "auto",
                          }}
                        >
                          {thisItemRecording && (isRecording || isPaused) ? (
                            <Tooltip title={t("stop")}>
                              <StopIcon
                                onClick={onStopRecording}
                                sx={{
                                  color: "#d32f2f",
                                  cursor: "pointer",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    color: "#a30000",
                                    transform: "scale(1.1)",
                                  },
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title={t("start")}>
                              <FiberManualRecordIcon
                                onClick={
                                  recordStartDisabled
                                    ? undefined
                                    : () => onStartRecording(item.id)
                                }
                                sx={{
                                  color: recordStartDisabled ? "#ccc" : "#d32f2f",
                                  cursor: recordStartDisabled
                                    ? "not-allowed"
                                    : "pointer",
                                  pointerEvents: recordStartDisabled
                                    ? "none"
                                    : "auto",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    color: recordStartDisabled ? "#ccc" : "#a30000",
                                    transform: recordStartDisabled
                                      ? "none"
                                      : "scale(1.1)",
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>
                      )}
                  </div>

                  {isRecording && thisItemRecording && (
                    <RecordingTimerBadge
                      active
                      paused={false}
                      startTimeMs={recordingStartTimeMs}
                    />
                  )}

                  {isPaused && thisItemRecording && (
                    <RecordingTimerBadge
                      active
                      paused
                      startTimeMs={recordingStartTimeMs}
                    />
                  )}

                  {item.existingItemRecording && !item.recordedBlob && (
                    <div style={{ marginTop: "12px", touchAction: "auto" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <audio
                          controls
                          src={item.existingItemRecording.recordingUrl ?? ""}
                          style={{ flex: 1 }}
                        />
                        <Tooltip title={t("formAction.delete")}>
                          <IconButton
                            size="small"
                            onClick={() => onRequestDeleteRecording(item.id)}
                            sx={{ color: "#d32f2f", marginLeft: 2 }}
                          >
                            <IconDeleteRed />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  )}

                  {item.recordedBlob && (
                    <ItemBlobAudio
                      blob={item.recordedBlob}
                      onRemove={() => onRequestDeleteRecording(item.id)}
                    />
                  )}

                  {thisItemRecording && (isRecording || isPaused) && (
                    <div
                      className="warning flex justify-content-start"
                      style={{ marginBottom: 12 }}
                    >
                      <IconWarningFormEdit />
                      <span
                        style={{ marginLeft: 12, flex: 1 }}
                        className="txt-black-1"
                      >
                        {t("warningUpdateItem3")}
                      </span>
                    </div>
                  )}

                  <div className="update-media-upload-file mt-24 mb-24 w-100">
                    <MediaUpload
                      key={`${item.id}-${mediaPreviewNonce}`}
                      id={item.id}
                      setFileCount={handleSetFileCount}
                      medias={
                        item.existingItemMedias
                          ? [...item.existingItemMedias, ...item.mediaFiles]
                          : item.mediaFiles
                      }
                      setHasUploadError={handleSetUploadError}
                      fileUploadLimit={fileUploadLimit}
                      showMaxUploadLimit={true}
                      errMessage={
                        mediaLimitExceeded
                          ? t("maxFilesExceeded", {
                              max: INSPECTION_MEDIA_TOTAL_LIMIT,
                            })
                          : ""
                      }
                      setMedias={handleSetMedias}
                      onPreviewMedia={onPreviewUploadMedia}
                      uploadDescription={uploadDescription}
                      maxFiles={5}
                    />
                  </div>

                  {item.source === "manual" && (
                    <div
                      className="d-flex justify-content-end mt-16"
                      style={{ marginRight: "-10px", marginTop: "15px" }}
                    >
                      <Button
                        className="btn-no-border-red btn-delete-card"
                        onClick={() => onRemoveItem(item.id)}
                      >
                        <IconDelete />
                        <span
                          className="txt-red ml-10 fs-14"
                          style={{ textTransform: "none" }}
                        >
                          {t("formAction.delete")}
                        </span>
                      </Button>
                    </div>
                  )}
                </Box>
              </Fade>
            </Collapse>
          </>
        )}
      </Box>
    );
  },
);
